import moment from 'moment'
import { shift } from '@floating-ui/dom'

// Define DATE_FORMATS locally since it's not imported
enum DATE_FORMATS {
  INPUT = 'MM-DD-YYYY',
  DISPLAY_SIMPLE = 'MM/DD/YYYY',
}

// This helper should stay local to DatePicker because of reliance on moment
export const parseDateStringToDate = (dateString: string | undefined) =>
  // This is the current reason we are reliant on moment for date picker - input parsing is much stricter in Luxon
  dateString && moment(dateString).isValid()
    ? moment(dateString).toDate()
    : undefined

// This helper should stay local to DatePicker because of reliance on moment
// Set it to the beginning of the day on selection
export const convertSelectedDateToString = (
  date: Date,
  format: DATE_FORMATS | string
) => moment(date).startOf('day').format(format)

// Takes the partial date input and starts formatting it before entry completion.  ie for format MM/DD/YYYY 06/23 will set the date but not year
export const formatPartialDateStringToMoment = (
  value: string,
  format: DATE_FORMATS | string
) => {
  // This helper will only work for formats that are 1:1 with length of format (ie MM/DD is 06/23).
  // It won't work for formats that aren't (ie M/D which can be 3/4 or 11/11)
  if (
    [DATE_FORMATS.INPUT, DATE_FORMATS.DISPLAY_SIMPLE].includes(
      format as DATE_FORMATS
    )
  ) {
    const partialDateMoment = moment.utc(
      value,
      format.substring(0, value?.length)
    )

    if (partialDateMoment.isValid()) {
      return partialDateMoment
    }
  }

  const parsedMomentFromString = moment.utc(value)

  return parsedMomentFromString.isValid() ? parsedMomentFromString : null
}

export const createDisplayValue = (
  value: string | [string, string] | undefined,
  selectsRange: boolean | undefined,
  start: Date | undefined,
  end: Date | undefined,
  dateFormat: string
) => {
  if (selectsRange) {
    if (start && end) {
      const startStr = convertSelectedDateToString(start, dateFormat)
      const endStr = convertSelectedDateToString(end, dateFormat)
      return `${startStr} - ${endStr}`
    } else if (start) {
      return convertSelectedDateToString(start, dateFormat)
    } else if (end) {
      return convertSelectedDateToString(end, dateFormat)
    }
    return ''
  }

  if (!value) {
    return ''
  }
  if (Array.isArray(value)) {
    const [startVal, endVal] = value
    if (startVal && endVal) {
      return `${startVal} - ${endVal}`
    } else if (startVal) {
      return startVal
    } else if (endVal) {
      return endVal
    }
    return ''
  }
  return value
}

export const createSelectedDate = (
  value: string | [string, string] | undefined,
  dateFormat: string
) => {
  if (!value || Array.isArray(value)) {
    return null
  }

  const parsedMoment = formatPartialDateStringToMoment(value, dateFormat)

  return parsedMoment?.isValid()
    ? new Date(parsedMoment.year(), parsedMoment.month(), parsedMoment.date())
    : null
}

export const createDateValue = (
  value: string | [string, string] | undefined,
  dateFormat: string
) => {
  const defaultDate = new Date().toISOString()
  if (!value || Array.isArray(value)) {
    return defaultDate
  }

  const parsedMoment = formatPartialDateStringToMoment(value, dateFormat)

  return parsedMoment?.isValid()
    ? new Date(
        parsedMoment.year(),
        parsedMoment.month(),
        parsedMoment.date()
      ).toISOString()
    : defaultDate
}

export const findSelectedOptionIndex = (
  options: Array<{ startDate: string; endDate: string }>,
  startDate: string | undefined,
  endDate: string | undefined
) => {
  const index = options.findIndex(
    (option) => option.startDate === startDate && option.endDate === endDate
  )
  return index === -1 ? undefined : index
}

export const createPopoverModifiers = () => [
  shift({
    boundary: document.querySelector('.content-wrapper') || undefined,
    padding: 10,
  }),
]
